/* eslint-disable @typescript-eslint/camelcase */
import type { UserFull } from '@/models/users'
import type { Company } from '@/models/companies'

export enum AnalyticsEvent {
  REGISTER_START = 'register_start',
  REGISTER_SUCCESS = '$registered',
  LOGIN = '$authorized',
  LOGOUT = 'logout',
  USER_RATING = 'USER_RATING',
}

interface AnalyticEventPayload<T = unknown> {
  user?: Partial<UserFull>
  additionalData?: T
}

const YANDEX_ANALYTIC_ID = 80460028

const getUserName = (user: Partial<UserFull>): string => {
  return `${user.contacts?.lname} ${user.contacts?.fname}`
}

const getUserRole = (user: Partial<UserFull>): string => {
  const currentRole = user.company && user.roles && user.roles[user.company]
  return currentRole ? currentRole.name : 'Unknown'
}

export const setCarrotQuestUserParams = (user: Partial<UserFull>): void => {
  try {
    const carrotService = window.carrotquest
    if (carrotService) {
      if (user.carrotquest_key && user.id) {
        carrotService.auth(user.id, user.carrotquest_key)
      }

      carrotService.identify({
        $name: getUserName(user),
        $email: user.email,
        $phone: user.contacts?.phone,
        firstName: user.contacts?.fname,
        lastName: user.contacts?.lname,
        role: getUserRole(user),
      })
    }
  } catch (e) {
    console.log(e)
  }
}

export const setCarrotQuestCompanyParams = (companies: Company[]): void => {
  try {
    window.carrotquest?.identify({
      companies: companies.map(c => c.title).join(', '),
    })
  } catch (e) {
    console.log(e)
  }
}

export const sendAnalyticEvent = <T = unknown>(
  event: AnalyticsEvent,
  payload: AnalyticEventPayload<T> = {},
): void => {
  try {
    const { user, additionalData } = payload

    switch (event) {
      case AnalyticsEvent.REGISTER_START:
        if (!user) {
          return
        }

        window.gtag?.('event', 'start', {
          event_category: 'reg',
        })
        window.ym?.(YANDEX_ANALYTIC_ID, 'reachGoal', 'user_reg_start')
        window.carrotquest?.track('register_start', {
          $email: user.email,
        })
        return
      case AnalyticsEvent.REGISTER_SUCCESS:
        if (!user) {
          return
        }

        window.gtag?.('event', 'finish', {
          event_category: 'reg',
        })
        window.ym?.(YANDEX_ANALYTIC_ID, 'reachGoal', 'user_reg_finish')

        window.carrotquest?.identify({ role: getUserRole(user) })
        window.carrotquest?.track('$registered', {
          $email: user.email,
          $name: getUserName(user),
        })
        return
      case AnalyticsEvent.LOGIN:
        if (!user) {
          return
        }

        window.gtag?.('event', 'auth', {
          event_category: 'reg',
        })
        window.ym?.(YANDEX_ANALYTIC_ID, 'reachGoal', 'user_auth')
        window.carrotquest?.track('$authorized', {
          $email: user.email,
          $name: getUserName(user),
        })
        return
      case AnalyticsEvent.LOGOUT:
        if (!user) {
          return
        }

        window.carrotquest?.track('$logout', {
          $email: user.email,
          $name: getUserName(user),
        })
        return
      case AnalyticsEvent.USER_RATING:
        window.carrotquest?.track('user_rating', {
          ...(user ? { $email: user.email, $name: getUserName(user) } : {}),
          rating: additionalData,
        })
        return
      default:
        return
    }
  } catch (e) {
    console.log(e)
  }
}
